/**
 * Phase two of the priority ladder: for each of the three that ended on top,
 * the follow-up group that asks what that priority actually means to them.
 *
 * A ranking on its own is thin evidence. "Compensation, then balance, then
 * growth" is true of most applicants; "balance means no on-call and vacation
 * people actually take" is the sentence a coach can do something with. So the
 * ladder settles the order and this settles the content, and only for the
 * three that carry the answer — asking all eight is the cognitive-load
 * failure the ticket's load budget exists to avoid.
 *
 * Every rule and every sentence lives here rather than in the component, for
 * the same reason as the ladder beside it: the copy is the feature.
 *
 * IDS, NEVER LABELS. A stored answer is a list of option ids, so rewording a
 * chip never rewrites an answer somebody already gave — the same fence the
 * ladder puts around its own cards. The design prototype stores the display
 * strings; that is the single thing this module most deliberately does not
 * copy from it.
 */

import type { CompRange } from './comp-range.js'
import { compRangeLine, isCompRange, settleCompRange } from './comp-range.js'
import {
  COMPANY_HITS,
  companyName,
  isCompanySlug,
  searchCompanies,
} from './companies.js'
import type { PriorityId } from './job-priorities.js'

/** One chip in a follow-up group. */
export interface FollowUpOption {
  id: string
  /** The applicant's own words for it, and what the admin recap prints. */
  label: string
  /**
   * Present on the one chip in a group that opens a text box beside it,
   * carrying that box's placeholder.
   *
   * A list of chips is a claim about what the answers can be, and for
   * "what kind of work would feel worth it" that claim is simply false —
   * there are more answers than we can name. The escape hatch is the chip
   * itself, so choosing it is one tap and typing is optional.
   *
   * One nested field rather than an `other` flag beside a loose
   * `placeholder`, because those two were co-required and nothing said so:
   * a placeholder without the flag drew no box, and the flag without a
   * placeholder drew a box with no hint. Neither is writable now.
   */
  freeText?: { placeholder: string }
}

/** A chip that opens a text box — what `otherOption` hands back. */
export type FreeTextOption = FollowUpOption & {
  freeText: { placeholder: string }
}

/** One pile of chips under a top-three priority. */
export interface FollowUpGroup {
  /**
   * Storage key, and unique across every group we ship — option ids are
   * scoped to their group, so `security` can mean a specialty in one pile
   * and national security in another without either answer moving.
   */
  id: string
  /** The question above the pile. */
  label: string
  /** Whether the pile takes more than one answer. */
  multi: boolean
  options: readonly FollowUpOption[]
  /**
   * Present on a pile the applicant can extend from the company list,
   * carrying the search box's placeholder.
   *
   * Prestige is the one priority whose honest answer set is far too long to
   * draw: the piles name the dozen or so companies most people mean, and this
   * reaches the rest. A pile with a search therefore accepts ids that are not
   * in `options` at all — every guard below that asks "is this id one of
   * ours" has to ask the company list too, which is exactly why this is a
   * nested object rather than a loose `searchable` flag. The flag alone would
   * have been readable as decoration.
   */
  search?: { placeholder: string }
  /**
   * This group is asked only while `group` holds `option`.
   *
   * The office-days pile makes no sense to somebody who did not say Hybrid,
   * and asking it anyway is how a form starts feeling like it is not
   * listening. Gates point BACKWARD at an earlier group in the same
   * priority, which is what keeps the check a lookup rather than a graph.
   */
  gate?: { group: string; option: string }
}

/** Shorthand for the many chips that are only an id and a label. */
const opt = (id: string, label: string): FollowUpOption => ({ id, label })

/**
 * A chip standing for one company, labelled from the company list rather than
 * spelled here.
 *
 * The named piles and the search hand back the SAME stored id, so a company
 * picked out of the FAANG pile and the same company found by typing are one
 * answer. Two parallel spellings of Google is how a recap reports one answer
 * twice and a reviewer counts it as two.
 */
const coOpt = (slug: string): FollowUpOption => ({
  id: slug,
  label: companyName(slug),
})

/**
 * The free-text escape hatch, spelled the same way everywhere it appears.
 * One helper so the three groups that offer one cannot drift apart on the
 * chip's own wording — the placeholder is the part that changes per group.
 */
const other = (placeholder: string): FreeTextOption => ({
  id: 'other',
  label: 'Other',
  freeText: { placeholder },
})

/**
 * Every follow-up pile, by the priority it hangs under.
 *
 * A total `Record` over `PriorityId` rather than a partial one: adding a
 * ninth priority should be a compile error here, naming the question nobody
 * has written yet. The two empty entries are not oversights — see below.
 *
 * Nothing offered here repeats a card on the ladder. Remote lives under work
 * flexibility and never under balance, which is what stops the two questions
 * quietly asking the same thing twice and counting it as two answers.
 */
export const FOLLOWUP_GROUPS: Record<PriorityId, readonly FollowUpGroup[]> = {
  // Compensation asks for a RANGE, which is not a pile of chips and so is not
  // in this table at all — see `FOLLOWUP_RANGES` below.
  comp: [],
  // Prestige names the companies most people mean, then lets them find the
  // rest. Three short piles rather than one long one: "FAANG" and "AI labs"
  // are the words applicants use for these sets, and a single forty-chip pile
  // is the cognitive-load failure the ticket's load budget exists to avoid.
  brand: [
    {
      id: 'brand.faang',
      label: 'FAANG',
      multi: true,
      options: [
        coOpt('meta'),
        coOpt('apple'),
        coOpt('amazon'),
        coOpt('netflix'),
        coOpt('google'),
      ],
    },
    {
      id: 'brand.adjacent',
      label: 'FAANG adjacent',
      multi: true,
      options: [
        coOpt('microsoft'),
        coOpt('nvidia'),
        coOpt('stripe'),
        coOpt('uber'),
        coOpt('airbnb'),
        coOpt('databricks'),
      ],
    },
    {
      id: 'brand.ai',
      label: 'AI labs',
      multi: true,
      options: [
        coOpt('openai'),
        coOpt('anthropic'),
        coOpt('xai'),
        coOpt('mistral'),
      ],
    },
    {
      id: 'brand.other',
      label: 'Anywhere else',
      multi: true,
      // The generic answer first, because "somewhere well-known" is a real
      // answer and a great many applicants mean exactly that rather than a
      // particular logo. The search is for the ones who do mean a logo.
      search: { placeholder: 'name any other company' },
      options: [opt('startup', 'A well-known / well-funded startup')],
    },
  ],
  balance: [
    {
      id: 'balance.what',
      label: 'What balance would have to mean',
      multi: true,
      options: [
        opt('under40', 'Under 40 hours a week'),
        opt('noOnCall', 'No on-call rotation'),
        opt('realVacation', 'Vacation people actually take'),
        opt('fewMeetings', 'Few meetings a day'),
        opt('greatManager', 'A great manager'),
      ],
    },
  ],
  advance: [
    {
      id: 'advance.what',
      label: 'Growth that would count',
      multi: true,
      options: [
        opt('levelLadder', 'A clear leveling ladder'),
        opt('staffTrack', 'Staff or tech lead track'),
        opt('managerTrack', 'Manager track'),
        opt('mentors', 'Mentors more senior than me'),
        opt('newDomain', 'A new domain to learn'),
      ],
    },
    {
      id: 'advance.when',
      label: 'Soonest promotion you would need',
      multi: false,
      options: [
        opt('within6Months', 'Inside 6 months'),
        opt('within1Year', 'Inside 1 year'),
        opt('within2Years', 'Inside 2 years'),
      ],
    },
  ],
  meaning: [
    {
      id: 'meaning.what',
      label: 'Work that would feel worth it',
      multi: true,
      options: [
        opt('health', 'Health and medicine'),
        opt('climate', 'Climate and energy'),
        opt('education', 'Education'),
        opt('research', 'Scientific research'),
        opt('publicGood', 'Safety and public good'),
        opt('government', 'Government or public service'),
        opt('nationalSecurity', 'National security'),
        opt('devTools', 'Tools other engineers use'),
        opt('ownProduct', 'A product I use myself'),
        other('what kind of work'),
      ],
    },
  ],
  stability: [
    {
      id: 'stability.years',
      label: 'Long-term means',
      multi: false,
      options: [
        opt('atLeast2', 'At least 2 years'),
        opt('atLeast5', 'At least 5 years'),
        opt('atLeast10', 'At least 10 years'),
      ],
    },
    {
      id: 'stability.signals',
      label: 'Signals you would look for',
      multi: true,
      options: [
        opt('funded', 'Profitable / 2+ yrs funded'),
        opt('noLayoffs', 'No layoffs recently'),
        opt('government', 'Government or a government contractor'),
        opt('largeAndBoring', 'Large and "boring"'),
      ],
    },
  ],
  switch: [
    {
      id: 'switch.into',
      label: 'Specialty you want to move into',
      multi: true,
      options: [
        opt('ml', 'ML / AI'),
        opt('infra', 'Infra / platform'),
        opt('security', 'Security'),
        opt('data', 'Data engineering'),
        opt('mobile', 'Mobile'),
        opt('frontend', 'Frontend'),
        opt('backend', 'Backend'),
        opt('fullStack', 'Full stack'),
        opt('embedded', 'Embedded / systems'),
        opt('games', 'Game development'),
        opt('research', 'Research'),
        other('which specialty'),
      ],
    },
  ],
  // The three arrangements ask for different things, so the two piles under
  // them open and close with the answer. Fully remote on its own asks for
  // nothing further, which is the point of it.
  flex: [
    {
      id: 'flex.arrange',
      label: 'Arrangements you would take',
      multi: true,
      options: [
        opt('remote', 'Fully remote'),
        opt('hybrid', 'Hybrid'),
        opt('onsite', 'Onsite is fine'),
      ],
    },
    {
      id: 'flex.days',
      label: 'Most office days a week you would accept',
      multi: false,
      gate: { group: 'flex.arrange', option: 'hybrid' },
      options: [
        opt('day1', '1 day'),
        opt('day2', '2 days'),
        opt('day3', '3 days'),
        opt('day4', '4 days'),
      ],
    },
    {
      id: 'flex.onsite',
      label: 'Onsite works if the job flexes some other way',
      multi: true,
      gate: { group: 'flex.arrange', option: 'onsite' },
      options: [
        opt('pickWork', 'I pick what I work on'),
        opt('ownHours', 'I set my own hours'),
        opt('deepWork', 'Deep work over back-to-back meetings'),
        opt('leaveWhenNeeded', 'Leave when I need it, no haggling'),
        other('what flexibility'),
      ],
    },
  ],
}

/**
 * A follow-up that is a pair of numbers on a track rather than a pile.
 *
 * Its own table rather than a variant of `FollowUpGroup`, because a range
 * shares almost nothing with a pile: no options, no multi, no gate, and none
 * of the tapping. A union of the two would have made every reader of
 * `group.options` narrow first, in return for one shared field.
 */
export interface RangeQuestion {
  /** Storage key, and unique across every follow-up we ship, piles included. */
  id: string
  /** The question above the track. */
  label: string
}

/**
 * The priorities whose follow-up is a range.
 *
 * Partial, unlike the pile table beside it: most priorities ask no range, and
 * a total record here would be six explicit `undefined`s saying nothing. The
 * pile table is total for the opposite reason — a ninth priority must be a
 * compile error there, naming the pile nobody has written yet.
 *
 * The money rules and every sentence around the track live in `comp-range.ts`.
 * This only says which priority asks the question.
 */
export const FOLLOWUP_RANGES: Partial<Record<PriorityId, RangeQuestion>> = {
  comp: {
    id: 'comp.range',
    label: 'Total compensation you would consider',
  },
}

/** The range asked under a priority, if it asks one. */
export function followUpRange(priorityId: string): RangeQuestion | undefined {
  return Object.hasOwn(FOLLOWUP_RANGES, priorityId)
    ? FOLLOWUP_RANGES[priorityId as PriorityId]
    : undefined
}

/**
 * Whether phase two has anything at all to ask about a priority.
 *
 * Asked of BOTH tables, which is the whole reason it exists: a priority whose
 * follow-up is a range declares no piles, and a caller that only counted
 * piles drew a bordered card with a heading and nothing in it — or, worse,
 * skipped compensation entirely while the ladder said it mattered most.
 */
export function asksFollowUps(priorityId: string): boolean {
  return (
    followUpGroups(priorityId).length > 0 ||
    followUpRange(priorityId) !== undefined
  )
}

/**
 * What the applicant said under the top three: group id → the option ids they
 * picked, and separately whatever they typed into an `other` box.
 *
 * Two bags rather than one so a list of ids stays a list of ids. Free text
 * mixed into the same array would make every reader of a picked option ask
 * whether this one is an id or a sentence, and the admin recap is where
 * getting that wrong prints `other` at a reviewer.
 */
export interface FollowUpAnswers {
  picks: Record<string, string[]>
  typed: Record<string, string>
  /**
   * Where the two thumbs are, by range-question id — a third bag for the same
   * reason there is a second: a pair of numbers mixed into a list of ids would
   * make every reader of a pick ask which kind of thing this one is.
   *
   * Optional, and absent rather than empty, the same way `followUps` itself
   * hangs off a ranking. Two reasons, and both matter. Every row written
   * before the range shipped has two bags and is still a whole answer, so a
   * required third would have made the day it shipped the day an old draft
   * stopped parsing — including an old tab still posting during a deploy.
   * And an untouched question must leave no scaffolding in the jsonb bag.
   *
   * Never `{}`: absent and empty are the same state and the type admits both
   * spellings of it, so every writer here goes through `bag`, which is what
   * keeps the two from drifting into "answered" and "not answered".
   */
  ranges?: Record<string, CompRange | undefined>
}

/** Nothing said yet. */
export function emptyFollowUps(): FollowUpAnswers {
  return { picks: {}, typed: {} }
}

/**
 * The three bags as one answer, with an empty third left off entirely.
 *
 * One builder for every writer below, so "absent rather than empty" is a rule
 * this module keeps rather than one each of five returns remembers.
 */
function bag(
  picks: Record<string, string[]>,
  typed: Record<string, string>,
  ranges: Record<string, CompRange | undefined> | undefined,
): FollowUpAnswers {
  const next: FollowUpAnswers = { picks, typed }
  if (ranges && Object.keys(ranges).length > 0) next.ranges = ranges
  return next
}

/** Every group under a priority, gates ignored. An unknown id has none. */
export function followUpGroups(priorityId: string): readonly FollowUpGroup[] {
  return Object.hasOwn(FOLLOWUP_GROUPS, priorityId)
    ? FOLLOWUP_GROUPS[priorityId as PriorityId]
    : []
}

/** One group by its storage key, across every priority. */
export function followUpGroup(groupId: string): FollowUpGroup | undefined {
  for (const groups of Object.values(FOLLOWUP_GROUPS))
    for (const group of groups) if (group.id === groupId) return group
  return undefined
}

/** Whether a group's gate is open — always, for a group without one. */
export function isGateOpen(
  group: FollowUpGroup,
  answers: FollowUpAnswers,
): boolean {
  if (!group.gate) return true
  return (answers.picks[group.gate.group] ?? []).includes(group.gate.option)
}

/**
 * The groups under a priority that are actually being asked right now.
 *
 * Filtered here, before anything is drawn, so the office-days pile can sit
 * directly under the arrangements pile and disappear the moment Hybrid does.
 */
export function openFollowUpGroups(
  priorityId: string,
  answers: FollowUpAnswers,
): readonly FollowUpGroup[] {
  return followUpGroups(priorityId).filter((g) => isGateOpen(g, answers))
}

/** A follow-up option as display text; an unknown id passes straight through. */
export function followUpOptionLabel(groupId: string, optionId: string): string {
  const group = followUpGroup(groupId)
  const named = group?.options.find((o) => o.id === optionId)?.label
  if (named !== undefined) return named
  // A pile with a search holds ids this table never named: every company
  // past the dozen drawn as chips. The company list is the rest of that
  // pile's vocabulary, and it is tolerant in the same way this line is — a
  // slug we have since dropped prints as itself rather than as nothing.
  if (group?.search) return companyName(optionId)
  return optionId
}

/**
 * Whether a pile still offers this id: its own chips, plus — for a pile with
 * a search — every company behind the box.
 *
 * The strict test, used on the way in. The tolerant readers above never ask
 * it, which is what lets a company we have since dropped keep rendering.
 */
function isKnownOption(group: FollowUpGroup, id: string): boolean {
  // Its own named chips first, so a pile still accepts the companies IT
  // names even though the clause below refuses them.
  if (group.options.some((o) => o.id === id)) return true
  // The exact mirror of what the search refuses to offer. Without the last
  // clause a crafted payload picks Google under the FAANG pile AND under
  // "Anywhere else", both survive, and the recap prints one applicant's one
  // answer twice under two headings — the double-print `NAMED_COMPANIES`
  // exists to prevent on the drawing side.
  return (
    group.search !== undefined &&
    isCompanySlug(id) &&
    !NAMED_COMPANIES.includes(id)
  )
}

/**
 * Every company that already has a chip of its own somewhere in the table.
 *
 * A search must never offer one of these: the applicant would be looking at
 * the same company twice on one card, able to pick it in both places, and the
 * recap would print their one answer under two headings.
 *
 * Derived rather than listed, so moving a company between the named piles —
 * or out of them — cannot leave a stale exclusion behind.
 */
const NAMED_COMPANIES: readonly string[] = Object.values(FOLLOWUP_GROUPS)
  .flat()
  .flatMap((group) => group.options.map((o) => o.id))
  .filter(isCompanySlug)

/** The chips a pile draws right now, and the line under its search box. */
export interface DrawnPile {
  /** Named chips first, then the ones on screen, then the closest matches. */
  options: readonly FollowUpOption[]
  /** What to say under the box. Never '' for a pile that has one. */
  hint: string
}

/**
 * What to draw for a pile, given what has been typed into its search box.
 *
 * A pile without a search is simply its own chips — the one call site can
 * then stay one call site rather than branching on a field it would have to
 * remember exists.
 *
 * A pile WITH one never draws the whole company list. It draws its named
 * chips, then whatever companies they have already picked so an answer does
 * not vanish when the box is cleared, then the closest few matches. The hint
 * is what stops a capped list reading as the complete one.
 */
export function drawnPile(
  group: FollowUpGroup,
  shown: readonly string[],
  query: string,
): DrawnPile {
  if (!group.search) return { options: group.options, hint: '' }
  const q = query.trim()
  // A picked company is NOT held back from its own search results. Excluded
  // there and re-drawn up here instead, the act of picking one moved its chip
  // several slots up the pile — and the node being moved is the button that
  // was just pressed, which drops focus onto <body> and slides a fresh chip
  // under a thumb about to tap again. Drawn once, where the search put it,
  // picking it changes `aria-pressed` and nothing else.
  const hits = q === '' ? [] : searchCompanies(q, NAMED_COMPANIES)
  const top = hits.slice(0, COMPANY_HITS).map((c) => c.slug)
  const held = shown.filter(
    (id) => !top.includes(id) && !group.options.some((o) => o.id === id),
  )
  return {
    options: [...group.options, ...held.map(coOpt), ...top.map(coOpt)],
    hint: searchHint(q, hits.length, held.length),
  }
}

/**
 * The line under a search box, which is never empty.
 *
 * Announced, so it has to carry the SUCCESS case too. Saying something only
 * when the search fails is how a screen-reader user hears "nothing matches"
 * and then, on the query that works, hears nothing at all — and has to leave
 * the box and walk the pile to find out whether anything happened.
 */
function searchHint(query: string, hits: number, held: number): string {
  if (query === '')
    return held > 0
      ? 'Search again to add another.'
      : 'Or search for the company you mean.'
  if (hits === 0) return `Nothing matches "${query}".`
  if (hits > COMPANY_HITS)
    return `Closest ${COMPANY_HITS} of ${hits}. Keep typing to narrow it.`
  return `${hits} ${hits === 1 ? 'company matches' : 'companies match'} "${query}".`
}

/** Whether a group offers the free-text chip. */
export function otherOption(group: FollowUpGroup): FreeTextOption | undefined {
  // Narrowed on the way out, so a caller drawing the box reaches its
  // placeholder without a second optional check the type already settled.
  return group.options.find(
    (o): o is FreeTextOption => o.freeText !== undefined,
  )
}

/** The id of the group's free-text chip, when it still offers one. */
function chipId(group: FollowUpGroup): string | undefined {
  return otherOption(group)?.id
}

/** Whether the free-text chip in this group is currently picked. */
export function isOtherPicked(
  group: FollowUpGroup,
  answers: FollowUpAnswers,
): boolean {
  const chip = otherOption(group)
  if (!chip) return false
  return (answers.picks[group.id] ?? []).includes(chip.id)
}

/** Whether a stored value is the follow-up shape, for the tolerant read path. */
export function isFollowUpAnswers(value: unknown): value is FollowUpAnswers {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return false
  const { picks, typed, ranges } = value as Record<string, unknown>
  const bagOf = (check: (v: unknown) => boolean) => (bag: unknown) =>
    typeof bag === 'object' &&
    bag !== null &&
    !Array.isArray(bag) &&
    Object.values(bag).every(check)
  return (
    bagOf((v) => Array.isArray(v) && v.every((x) => typeof x === 'string'))(
      picks,
    ) &&
    bagOf((v) => typeof v === 'string')(typed) &&
    // Absent is fine and always will be: every row written before the range
    // existed has two bags, and demanding three here would be the day the
    // range shipped and every older answer rendered as blank.
    (ranges === undefined || bagOf(isCompRange)(ranges))
  )
}

/**
 * A stored bag put in order: junk rejected, each option kept once per group,
 * and a single-answer group cut to its first pick.
 *
 * The TOLERANT half of the pair, matching the ladder's `settlePriorities`.
 * An option id we have since retired survives on purpose — the row is still
 * that applicant's answer, `followUpOptionLabel` is built to print an id it
 * does not recognise, and the ticket asks that an answer to a question we
 * later removed still renders. `knownFollowUps` is the half that restricts to
 * what we offer today.
 *
 * A group that LOST `multi` is the reason single-answer groups are cut here
 * rather than only on the way in: a draft saved while the pile took several
 * answers would otherwise hand the drawing half two selected chips in a pile
 * that can only show one.
 */
export function settleFollowUps(value: unknown): FollowUpAnswers {
  if (!isFollowUpAnswers(value)) return emptyFollowUps()
  const picks: Record<string, string[]> = {}
  for (const [groupId, ids] of Object.entries(value.picks)) {
    const once = [...new Set(ids)]
    const group = followUpGroup(groupId)
    // A group we no longer recognise keeps every pick: we cannot know whether
    // it took one answer or many, and dropping all but the first would edit
    // an answer rather than read it.
    const kept = group && !group.multi ? once.slice(0, 1) : once
    if (kept.length > 0) picks[groupId] = kept
  }
  const typed: Record<string, string> = {}
  for (const [groupId, text] of Object.entries(value.typed)) {
    // Kept EXACTLY as typed, whitespace and all. This runs on every
    // keystroke — the widget draws from it — so trimming here would hand a
    // controlled input back a string the applicant did not type, and the
    // space they pressed between two words would vanish as they pressed it.
    // A box holding only whitespace is still nothing said, so that one goes.
    // Final trimming belongs on the wire (`priorityValueSchema`) and on the
    // way out (`followUpLine`), both of which run once rather than per key.
    if (text.trim() !== '') typed[groupId] = text
  }
  const ranges: Record<string, CompRange> = {}
  for (const [questionId, range] of Object.entries(value.ranges ?? {})) {
    // Settled by its own module, which is where the grid and the track's two
    // ends live. Junk under a key answers undefined and the key goes away —
    // an untouched range must not become a floor of zero.
    const settled = settleCompRange(range)
    if (settled) ranges[questionId] = settled
  }
  return bag(picks, typed, ranges)
}

/**
 * Settled, then cut down to the groups and options we ship today.
 *
 * The STRICT half, and the one the wire schema uses: the server is where an
 * option id we never shipped gets refused. Everything that merely READS an
 * answer goes through `settleFollowUps` and keeps the retired id.
 *
 * Typed text is kept only where the group still offers a text box AND that
 * box's chip is still picked, so a sentence cannot outlive the question it
 * answered and surface under a chip nobody chose.
 */
export function knownFollowUps(value: unknown): FollowUpAnswers {
  const settled = settleFollowUps(value)
  const picks: Record<string, string[]> = {}
  for (const [groupId, ids] of Object.entries(settled.picks)) {
    const group = followUpGroup(groupId)
    if (!group) continue
    const kept = ids.filter((id) => isKnownOption(group, id))
    if (kept.length > 0) picks[groupId] = kept
  }
  const typed: Record<string, string> = {}
  const known: FollowUpAnswers = { picks, typed }
  for (const [groupId, text] of Object.entries(settled.typed)) {
    const group = followUpGroup(groupId)
    if (group && isOtherPicked(group, known)) typed[groupId] = text
  }
  const ranges: Record<string, CompRange> = {}
  for (const [questionId, range] of Object.entries(settled.ranges ?? {}))
    if (range && isRangeQuestionId(questionId)) ranges[questionId] = range
  return bag(picks, typed, ranges)
}

/** Whether a range question with this storage key is one we still ask. */
function isRangeQuestionId(id: string): boolean {
  return Object.values(FOLLOWUP_RANGES).some((q) => q.id === id)
}

/**
 * Every id this pile can currently hold, in the order a stored answer should
 * read: the named chips in the order they are drawn, then anything already
 * picked that the table never named, then the id being tapped if it is new.
 *
 * The second and third parts are what a pile with a search needs. Rebuilding
 * from `options` alone — which is all a chips-only pile ever needed — deletes
 * every company they found by typing on the very next tap of any chip in the
 * pile, because a company the table does not name is not in `options`.
 *
 * Found companies keep the order they were added in, which is the only order
 * they have: unlike the named chips there is no drawn sequence to match.
 */
function pileOrder(
  group: FollowUpGroup,
  picked: readonly string[],
  tapped: string,
): string[] {
  const named = group.options.map((o) => o.id)
  const order = [...named, ...picked.filter((id) => !named.includes(id))]
  if (!order.includes(tapped)) order.push(tapped)
  return order
}

/**
 * Tap a chip.
 *
 * Multi-answer groups rebuild from `options` rather than appending, so the
 * stored array always reads in the order the chips are drawn — otherwise
 * deselect-then-reselect silently reorders the answer. Single-answer groups
 * toggle: tapping the picked chip again clears it, because a pile with no
 * "none of these" chip is otherwise a decision nobody can take back.
 */
export function tapFollowUp(
  answers: FollowUpAnswers,
  group: FollowUpGroup,
  optionId: string,
): FollowUpAnswers {
  const picked = answers.picks[group.id] ?? []
  const on = picked.includes(optionId)
  const next = group.multi
    ? pileOrder(group, picked, optionId).filter((id) =>
        id === optionId ? !on : picked.includes(id),
      )
    : on
      ? []
      : [optionId]
  const picks = { ...answers.picks }
  if (next.length > 0) picks[group.id] = next
  else delete picks[group.id]
  // Un-picking the free-text chip drops what was typed under it. The sentence
  // only ever meant anything as the content of that chip, so leaving it
  // behind would let a withdrawn answer ride along to a reviewer.
  const chip = otherOption(group)
  const typed = { ...answers.typed }
  if (chip && !next.includes(chip.id)) delete typed[group.id]
  return bag(picks, typed, answers.ranges)
}

/** Type into a group's free-text box. */
export function typeFollowUp(
  answers: FollowUpAnswers,
  group: FollowUpGroup,
  text: string,
): FollowUpAnswers {
  const typed = { ...answers.typed }
  // Stored as typed, including the spaces mid-edit — trimming on every
  // keystroke is what stops somebody putting a space between two words.
  // `settleFollowUps` is where a blank one goes away.
  if (text !== '') typed[group.id] = text
  else delete typed[group.id]
  return bag({ ...answers.picks }, typed, answers.ranges)
}

/**
 * Move one thumb on a range question.
 *
 * The first move is what turns a range from something we DREW into something
 * they said: nothing is under the key until this runs, so the widget's
 * starting position never reaches the admin recap as their answer.
 */
export function setFollowUpRange(
  answers: FollowUpAnswers,
  question: RangeQuestion,
  range: CompRange,
): FollowUpAnswers {
  // Settled HERE rather than trusted from the caller. `CompRange` is two
  // numbers and `NaN` is a number, so the type promises nothing about the
  // grid, the track's two ends, or the floor staying under the ceiling —
  // today's one caller hands over `moveComp` output and is fine, and the
  // second one written will not be. Idempotent, so this costs that caller
  // nothing.
  const settled = settleCompRange(range)
  const ranges = { ...answers.ranges }
  if (settled) ranges[question.id] = settled
  else delete ranges[question.id]
  return bag({ ...answers.picks }, { ...answers.typed }, ranges)
}

/** Whether anything at all has been said under the top three. */
export function hasFollowUpAnswers(answers: FollowUpAnswers): boolean {
  const settled = settleFollowUps(answers)
  return (
    Object.keys(settled.picks).length > 0 ||
    Object.keys(settled.typed).length > 0 ||
    settled.ranges !== undefined
  )
}

/**
 * What one priority's follow-ups say, as one line, or '' when it says nothing.
 *
 * Only the groups currently being ASKED are read. A pile whose gate has since
 * closed keeps its answer in storage — un-tapping Hybrid by mistake must not
 * cost the office-day count, and tapping it back must bring the count back —
 * but it is no longer a claim the applicant is making, and printing it at a
 * reviewer would report an answer to a question we stopped asking.
 */
export function followUpLine(
  priorityId: string,
  answers: FollowUpAnswers,
): string {
  const settled = settleFollowUps(answers)
  const parts: string[] = []
  // The range first, because it is drawn first and because "$150k floor"
  // reads as the headline of what compensation means to somebody rather than
  // as a detail tacked onto the end of a list of chips.
  const range = followUpRange(priorityId)
  const said = range && settled.ranges?.[range.id]
  if (said) parts.push(compRangeLine(said))
  for (const group of openFollowUpGroups(priorityId, settled)) {
    const ids = settled.picks[group.id] ?? []
    // The ONE pick the group's one typed sentence belongs to, chosen per
    // group rather than per pick because `typed` is keyed by group and there
    // is therefore only ever one sentence to place. Normally today's
    // free-text chip. Once that chip has been RETIRED the sentence is still
    // in the bag and the picks we no longer ship are all that is left to
    // carry it — the first of them takes it, so a second retired pick keeps
    // printing its own id instead of a copy of somebody's sentence. Without
    // an owner at all a reviewer reads the bare option id and never the
    // answer, which is what the tolerant read path exists to prevent.
    const owner =
      chipId(group) ?? ids.find((id) => !group.options.some((o) => o.id === id))
    for (const id of ids) {
      // The typed sentence replaces the word "Other", which tells a reviewer
      // nothing. With the chip picked and nothing typed, the word is all
      // there is, and it is still the honest answer: they said none of ours.
      // Trimmed here, not in `settleFollowUps`: that one runs per keystroke
      // and must hand the input back what was typed. This runs once, on the
      // way out, which is where a stray space stops being their cursor and
      // starts being a reviewer's problem.
      const text = id === owner ? settled.typed[group.id]?.trim() : undefined
      parts.push(text || followUpOptionLabel(group.id, id))
    }
  }
  return parts.join(', ')
}
